import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ArrowRightIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import topUpUserBalance from '../api/topUpBalance';
import { getAdminUsers, getAdminUserById } from '../api/adminUsers';
import { TypeBadge, UserAvatar } from '../components/users/UserDetailsPanel';
import { parseServerDate } from '../utils/time';

const PRESETS = [1000, 5000, 10000, 50000];
const LARGE_AMOUNT = 100000;

const TRANSACTION_TYPES = {
  BALANCE_TOP_UP: 'Пополнение',
  PROMOTION_SPENDING: 'Продвижение',
  REFUND: 'Возврат',
  ORDER_PAYMENT: 'Оплата заказа',
  COMMISSION: 'Комиссия',
  BUSINESS_CREDIT: 'Бизнес-кредит',
  WITHDRAWAL_REQUEST: 'Заявка на вывод',
  WITHDRAWAL_COMPLETED: 'Вывод средств',
  WITHDRAWAL_REJECTED: 'Вывод отклонён',
  SUBSCRIPTION_PURCHASE: 'Подписка',
};

const money = (v) => `${Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₸`;
const fmtDate = (iso) =>
  iso ? parseServerDate(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** «12 500,50» → 12500.5 */
const parseAmount = (raw) => {
  const n = parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};

const BalanceManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [amount, setAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [lastTopUp, setLastTopUp] = useState(null);
  const amountRef = useRef(null);

  const selectUser = useCallback(async (id) => {
    setLoadingUser(true);
    setError(null);
    setResults([]);
    setConfirming(false);
    setLastTopUp(null);
    try {
      const full = await getAdminUserById(id);
      setUser(full);
      setQuery('');
      setTimeout(() => amountRef.current?.focus(), 0);
    } catch (err) {
      setSearchError(err.status === 404 ? `Пользователь #${id} не найден` : `Ошибка: ${err.detail || err.message}`);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  // /balance?user=123 — переход из карточки пользователя
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('user');
    if (id && /^\d+$/.test(id)) selectUser(id);
  }, [location.search, selectUser]);

  // Поиск по имени / телефону / email с задержкой; чистое число — это ID
  useEffect(() => {
    const q = query.trim();
    setSearchError(null);
    if (!q || /^#?\d{1,7}$/.test(q)) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await getAdminUsers({ q, limit: 8 });
        setResults(data.items || []);
        if (!data.items?.length) setSearchError('Никого не найдено');
      } catch (err) {
        setSearchError(`Ошибка поиска: ${err.detail || err.message}`);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim().replace(/^#/, '');
    if (/^\d{1,7}$/.test(q)) selectUser(q);
    else if (results.length === 1) selectUser(results[0].id);
  };

  const clearUser = () => {
    setUser(null);
    setAmount('');
    setConfirming(false);
    setError(null);
    setLastTopUp(null);
    if (location.search) navigate({ pathname: location.pathname }, { replace: true });
  };

  const value = parseAmount(amount);
  const amountValid = value > 0;
  const isLarge = amountValid && value >= LARGE_AMOUNT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !amountValid) {
      setError(!user ? 'Выберите пользователя.' : 'Введите сумму больше нуля.');
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await topUpUserBalance(user.id, value);
      setLastTopUp({ amount: value, before: user.balance });
      setAmount('');
      setConfirming(false);
      // Обновляем баланс и историю из backend
      setUser(await getAdminUserById(user.id));
    } catch (err) {
      setError(err.status === 403 ? 'Недостаточно прав для пополнения баланса.' : err.detail || err.message);
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 text-left">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Пополнение баланса</h2>
        <p className="mt-0.5 text-sm text-text-secondary">Зачисление средств на внутренний баланс пользователя. Операция записывается в историю транзакций.</p>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          {/* Step 1 — user */}
          <section className="rounded-block border border-border-primary bg-bg-card p-5 shadow-sm">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-grad text-[11px] text-white">1</span>
              Пользователь
            </h3>

            {user ? (
              <div className={`flex items-center gap-3 rounded-lg border p-3 ${user.is_active === false ? 'border-rose-500/40 bg-rose-500/[0.06]' : 'border-border-primary bg-bg-primary'}`}>
                <UserAvatar user={user} size="h-12 w-12 text-base" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-text-primary">{user.name || 'Без имени'}</span>
                    <TypeBadge type={user.user_type} />
                  </div>
                  <p className="truncate text-xs text-text-secondary">
                    <span className="font-mono">#{user.id}</span>
                    {user.phone_number && ` · ${user.phone_number}`}
                    {user.email && ` · ${user.email}`}
                  </p>
                  {user.is_active === false && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-rose-500">
                      <NoSymbolIcon className="h-3.5 w-3.5" /> Аккаунт деактивирован
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-text-muted">Баланс</p>
                  <p className="text-lg font-bold tabular-nums text-emerald-500">{money(user.balance)}</p>
                </div>
                <button type="button" onClick={clearUser} aria-label="Выбрать другого" title="Выбрать другого" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <form onSubmit={submitSearch} className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-text-muted" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ID, телефон, имя или email — Enter для поиска по ID"
                  aria-label="Поиск пользователя"
                  className="w-full rounded-lg border border-border-primary bg-bg-primary py-2.5 pl-9 pr-10 text-sm text-text-primary placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30"
                />
                {(searching || loadingUser) && <ArrowPathIcon className="absolute right-3 top-3 h-4 w-4 animate-spin text-text-muted" />}

                {results.length > 0 && (
                  <ul className="mt-2 divide-y divide-border-primary overflow-hidden rounded-lg border border-border-primary">
                    {results.map((u) => (
                      <li key={u.id}>
                        <button type="button" onClick={() => selectUser(u.id)} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-bg-tertiary">
                          <UserAvatar user={u} size="h-8 w-8 text-xs" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-text-primary">
                              {u.name || 'Без имени'} <span className="font-mono text-xs text-text-muted">#{u.id}</span>
                            </p>
                            <p className="truncate text-xs text-text-secondary">{u.phone_number || u.email || '—'}</p>
                          </div>
                          <span className="shrink-0 text-xs tabular-nums text-text-secondary">{money(u.balance)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {searchError && <p className="mt-2 text-xs text-text-muted">{searchError}</p>}
                {/^#?\d{1,7}$/.test(query.trim()) && (
                  <p className="mt-2 text-xs text-text-muted">
                    Нажмите Enter, чтобы открыть пользователя #{query.trim().replace('#', '')}
                  </p>
                )}
              </form>
            )}
          </section>

          {/* Step 2 — amount */}
          <form onSubmit={handleSubmit} className={`rounded-block border border-border-primary bg-bg-card p-5 shadow-sm ${user ? '' : 'pointer-events-none opacity-50'}`}>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-grad text-[11px] text-white">2</span>
              Сумма
            </h3>

            <div className="relative">
              <input
                ref={amountRef}
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value.replace(/[^\d.,\s]/g, ''));
                  setConfirming(false);
                  setError(null);
                }}
                placeholder="0"
                aria-label="Сумма пополнения"
                className="w-full rounded-lg border border-border-primary bg-bg-primary py-3 pl-4 pr-10 text-2xl font-semibold tabular-nums text-text-primary placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xl text-text-muted">₸</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    setAmount(String(p));
                    setConfirming(false);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-medium tabular-nums transition-colors ${
                    value === p ? 'border-[#4F45E5]/50 bg-[#4F45E5]/15 text-text-primary' : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
                  }`}
                >
                  +{p.toLocaleString('ru-RU')}
                </button>
              ))}
            </div>

            {user && amountValid && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-bg-primary px-4 py-3 text-sm">
                <span className="tabular-nums text-text-secondary">{money(user.balance)}</span>
                <ArrowRightIcon className="h-4 w-4 text-text-muted" />
                <span className="text-lg font-bold tabular-nums text-emerald-500">{money(Number(user.balance || 0) + value)}</span>
              </div>
            )}

            {isLarge && !confirming && (
              <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                Крупная сумма — проверьте количество нулей.
              </p>
            )}

            {error && (
              <p role="alert" className="mt-3 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                {error}
              </p>
            )}

            {confirming ? (
              <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                <p className="text-sm text-text-primary">
                  Зачислить <b className="tabular-nums">{money(value)}</b> пользователю <b>{user?.name || 'Без имени'}</b> (#{user?.id})? Отменить операцию из админки нельзя.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="submit"
                    autoFocus
                    disabled={sending}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {sending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <BanknotesIcon className="h-4 w-4" />}
                    {sending ? 'Зачисление…' : 'Да, пополнить'}
                  </button>
                  <button type="button" disabled={sending} onClick={() => setConfirming(false)} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="submit"
                disabled={!user || !amountValid}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <BanknotesIcon className="h-4 w-4" />
                Пополнить{amountValid ? ` на ${money(value)}` : ''}
              </button>
            )}

            {lastTopUp && (
              <p role="status" className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-500">
                <CheckCircleIcon className="h-5 w-5 shrink-0" />
                Зачислено {money(lastTopUp.amount)}. Баланс: {money(lastTopUp.before)} → {money(user?.balance)}
              </p>
            )}
          </form>
        </div>

        {/* History */}
        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm xl:col-span-2">
          <h3 className="border-b border-border-primary px-5 py-3.5 text-sm font-semibold text-text-primary">Последние операции</h3>
          {!user ? (
            <div className="px-6 py-12 text-center text-text-muted">
              <UserIcon className="mx-auto mb-2 h-8 w-8" />
              <p className="text-sm">Выберите пользователя, чтобы увидеть его транзакции</p>
            </div>
          ) : !user.recent_transactions ? (
            <p className="px-5 py-8 text-center text-sm text-text-muted">История недоступна на этой версии backend</p>
          ) : user.recent_transactions.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-text-muted">Транзакций пока нет</p>
          ) : (
            <ul className="divide-y divide-border-primary">
              {user.recent_transactions.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 px-5 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text-primary">{TRANSACTION_TYPES[t.type] || t.type}</p>
                    <p className="text-xs text-text-muted">{fmtDate(t.created_at)}</p>
                  </div>
                  <span className={`shrink-0 text-sm font-semibold tabular-nums ${t.amount >= 0 ? 'text-emerald-500' : 'text-text-primary'}`}>
                    {t.amount > 0 ? '+' : ''}
                    {money(t.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default BalanceManagement;
